/**
 * GroupLeaderboardList
 * Presentational list of leaderboard entries (rows). Used by Group Leaderboard screen.
 * Table layout: Rank | Player | Odds | Games | Wins (wins = main, purple).
 * Row actions: 3-dot menu (top right) for every other member (not self).
 * Unified FlashList: leaderboard + challenge history + leave footer as one flat list.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Dimensions, Pressable } from 'react-native'
import { useRouter } from 'expo-router'
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants'
import { getGroupLeaderboardStyles } from '../../../appStyles/_groupLeaderboard.styles'
import { useTheme } from '../../../src/theme/useTheme'
import type { GroupLeaderboardEntry } from '../../../src/friends/helpers/groupLeaderboard.helper'
import { getAcceptedParticipantIds } from '../../../src/friends/helpers/groupLeaderboard.helper'
import {
  GROUP_LEADERBOARD_EMPTY_MESSAGE,
  GROUP_CHALLENGE_HISTORY_TITLE,
  GROUP_CHALLENGE_HISTORY_EMPTY_MESSAGE,
} from '../../../src/friends/constants/groups.constants'
import type { ChallengeWithStats } from '../../../types/challenges'
import { getDefaultOpponentIdForChallenge } from '../GroupChallengeHistorySection/GroupChallengeHistorySection'
import { GroupChallengeHistoryCard } from '../GroupChallengeHistorySection/GroupChallengeHistoryCard'
import {
  type GroupUnifiedListItem,
  getUnifiedItemType,
} from './GroupLeaderboardList.types'
import { usePlayerRanks } from '../../../src/economy/hooks/usePlayerRanks'
import { LeaderboardRow } from './LeaderboardRow'
import { LeaderboardRowMenu } from './LeaderboardRowMenu'
import { LeaderboardTableHeader } from './LeaderboardTableHeader'
import type { FriendStatus } from '../../../src/friends/helpers/friendStatusMap.helper'
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types'

export type { FriendStatus } from '../../../src/friends/helpers/friendStatusMap.helper'
export type { GroupUnifiedListItem } from './GroupLeaderboardList.types'

export interface GroupLeaderboardListProps {
  entries: GroupLeaderboardEntry[]
  userId: string
  myDisplayName: string
  currentAvatarId: string | null
  /** Current user's custom layered look; overrides currentAvatarId for the self row/cards. */
  currentAvatarConfig?: EquippedMap | null
  creatorId?: string
  friendStatusByUserId?: Record<string, FriendStatus>
  /** While set, "Add friend" is disabled for that user id (request in flight). */
  friendRequestSendingUserId?: string | null
  /** While true, "Remove from group" is disabled on all rows (removal in flight). */
  removeMemberSending?: boolean
  onRemoveMember?: (entry: GroupLeaderboardEntry) => void
  onAddFriend?: (entry: GroupLeaderboardEntry) => void
  challenges?: ChallengeWithStats[]
  memberNames?: Record<string, string>
  onLeaveGroup?: () => void
  onLoadMore?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  listHeaderComponent?: React.ReactElement
}

export function GroupLeaderboardList({
  entries,
  userId,
  myDisplayName,
  currentAvatarId,
  currentAvatarConfig = null,
  creatorId,
  friendStatusByUserId,
  friendRequestSendingUserId = null,
  removeMemberSending = false,
  onRemoveMember,
  onAddFriend,
  challenges = [],
  memberNames = {},
  onLeaveGroup,
  onLoadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  listHeaderComponent,
}: GroupLeaderboardListProps) {
  const theme = useTheme()
  const router = useRouter()
  const styles = React.useMemo(() => getGroupLeaderboardStyles(theme), [theme])
  const [menuOpenForUserId, setMenuOpenForUserId] = useState<string | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null)
  const menuTriggerRefs = useRef<Record<string, View | null>>({})
  const hasUserScrolledRef = useRef(false)

  const [selectedOpponentByChallengeId, setSelectedOpponentByChallengeId] = useState<
    Record<string, string | null>
  >(() => ({}))

  // One batch for the members on screen (specs/rank-on-social-screens AC-9/AC-15).
  const { getPlayerRank } = usePlayerRanks(useMemo(() => entries.map((e) => e.userId), [entries]))

  const acceptedByChallengeId = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const c of challenges) {
      map.set(c.id, getAcceptedParticipantIds(c))
    }
    return map
  }, [challenges])

  useEffect(() => {
    setSelectedOpponentByChallengeId((prev) => {
      const next = { ...prev }
      for (const c of challenges) {
        const accepted = acceptedByChallengeId.get(c.id)
        if (!accepted) continue
        const current = next[c.id]
        const isValid = current && current !== userId && accepted.has(current)
        if (isValid) continue
        next[c.id] = getDefaultOpponentIdForChallenge(accepted, userId, entries)
      }
      return next
    })
  }, [challenges, userId, entries, acceptedByChallengeId])

  const handleEndReached = useCallback(() => {
    if (!hasUserScrolledRef.current) return
    if (hasNextPage && !isFetchingNextPage) onLoadMore?.()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  const handleScroll = useCallback(() => { hasUserScrolledRef.current = true }, [])

  const handleSelectParticipant = useCallback((challengeId: string, selectedUserId: string) => {
    setSelectedOpponentByChallengeId((prev) => ({ ...prev, [challengeId]: selectedUserId }))
  }, [])

  const listData = useMemo((): GroupUnifiedListItem[] => {
    const items: GroupUnifiedListItem[] = [{ type: 'table_header' }]
    if (entries.length > 0) {
      entries.forEach((entry) => items.push({ type: 'leaderboard_row', entry }))
    } else {
      items.push({ type: 'empty_leaderboard' })
    }
    items.push({ type: 'history_title' })
    if (challenges.length > 0) {
      challenges.forEach((challenge) => {
        items.push({ type: 'challenge_card', challenge, selectedId: selectedOpponentByChallengeId[challenge.id] ?? null })
      })
    } else {
      items.push({ type: 'empty_history' })
    }
    items.push({ type: 'leave_footer' })
    return items
  }, [entries, challenges, selectedOpponentByChallengeId])

  useEffect(() => {
    return () => {
      setMenuOpenForUserId(null)
      setMenuPosition(null)
    }
  }, [])

  const handleOpenMenu = useCallback((entry: GroupLeaderboardEntry) => {
    const ref = menuTriggerRefs.current[entry.userId]
    if (ref) {
      ref.measureInWindow((x, y, width, height) => {
        const { width: screenWidth } = Dimensions.get('window')
        setMenuPosition({ top: y + height, right: screenWidth - (x + width) })
        setMenuOpenForUserId(entry.userId)
      })
    } else {
      setMenuOpenForUserId(entry.userId)
      setMenuPosition({ top: 100, right: 16 })
    }
  }, [])

  const handleCloseMenu = useCallback(() => {
    setMenuOpenForUserId(null)
    setMenuPosition(null)
  }, [])

  const renderItemUnified = useCallback(
    ({ item }: ListRenderItemInfo<GroupUnifiedListItem>) => {
      switch (item.type) {
        case 'table_header':
          return (
            <View style={styles.tableHeaderContainer}>
              <LeaderboardTableHeader styles={styles} iconColor={theme.gray[600]} />
            </View>
          )
        case 'leaderboard_row':
          return (
            <LeaderboardRow
              entry={item.entry}
              userId={userId}
              myDisplayName={myDisplayName}
              currentAvatarId={currentAvatarId}
              currentAvatarConfig={currentAvatarConfig}
              playerRank={getPlayerRank(item.entry.userId)}
              styles={styles}
              theme={theme}
              menuTriggerRef={(r) => { menuTriggerRefs.current[item.entry.userId] = r }}
              onOpenMenu={handleOpenMenu}
            />
          )
        case 'empty_leaderboard':
          return (
            <View style={styles.empty}>
              <Text style={styles.emptyText}>{GROUP_LEADERBOARD_EMPTY_MESSAGE}</Text>
            </View>
          )
        case 'history_title':
          return (
            <View style={styles.challengeHistorySection}>
              <Text style={styles.challengeHistoryTitle}>{GROUP_CHALLENGE_HISTORY_TITLE}</Text>
            </View>
          )
        case 'challenge_card': {
          const accepted = acceptedByChallengeId.get(item.challenge.id) ?? new Set<string>()
          return (
            <GroupChallengeHistoryCard
              challenge={item.challenge}
              selectedId={item.selectedId}
              currentUserId={userId}
              leaderboardEntries={entries}
              memberNames={memberNames}
              userAvatarId={currentAvatarId}
              userAvatarConfig={currentAvatarConfig}
              acceptedSet={accepted}
              onSelectParticipant={handleSelectParticipant}
              cardWrapStyle={styles.challengeHistoryCardWrap}
              testID="group-challenge-history"
            />
          )
        }
        case 'empty_history':
          return (
            <View style={styles.challengeHistoryEmpty}>
              <Text style={styles.challengeHistoryEmptyText}>{GROUP_CHALLENGE_HISTORY_EMPTY_MESSAGE}</Text>
              <Pressable
                style={({ pressed }) => [
                  styles.challengeHistoryShowMoreButton,
                  pressed && styles.challengeHistoryShowMoreButtonPressed,
                ]}
                onPress={() => router.push('/app/create')}
              >
                <Text style={styles.challengeHistoryShowMoreText}>Start a Challenge</Text>
              </Pressable>
            </View>
          )
        case 'leave_footer':
          return (
            <View style={styles.leaveGroupFooter}>
              <Pressable
                onPress={() => onLeaveGroup?.()}
                style={styles.leaveGroupButton}
                accessibilityLabel="Leave group"
              >
                <Text style={styles.leaveGroupButtonText}>Leave group</Text>
              </Pressable>
            </View>
          )
        default:
          return null
      }
    },
    [
      styles, theme, userId, myDisplayName, currentAvatarId, currentAvatarConfig,
      handleOpenMenu, acceptedByChallengeId, entries, memberNames,
      handleSelectParticipant, onLeaveGroup, router, getPlayerRank,
    ]
  )

  const keyExtractorUnified = useCallback((item: GroupUnifiedListItem, index: number) => {
    if (item.type === 'leaderboard_row') return `user-${item.entry.userId}`
    if (item.type === 'challenge_card') return `challenge-${item.challenge.id}`
    return `${item.type}-${index}`
  }, [])

  const ListFooterComponent = useMemo(() => {
    if (!isFetchingNextPage) return null
    return (
      <View style={styles.challengeHistoryLoadMoreFooter}>
        <Text style={styles.challengeHistoryLoadMoreText}>Loading more…</Text>
      </View>
    )
  }, [isFetchingNextPage, styles.challengeHistoryLoadMoreFooter, styles.challengeHistoryLoadMoreText])

  const rowMenuEntry =
    menuOpenForUserId && menuPosition ? entries.find((e) => e.userId === menuOpenForUserId) : null

  return (
    <View style={styles.listWrapper}>
      <FlashList
        overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
        data={listData}
        renderItem={renderItemUnified}
        keyExtractor={keyExtractorUnified}
        getItemType={getUnifiedItemType}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.4}
        onScroll={handleScroll}
        ListHeaderComponent={listHeaderComponent}
        ListFooterComponent={ListFooterComponent}
      />
      {rowMenuEntry && menuPosition && (
        <LeaderboardRowMenu
          entry={rowMenuEntry}
          position={menuPosition}
          friendStatus={friendStatusByUserId?.[rowMenuEntry.userId] ?? null}
          isFriendRequestSending={
            friendRequestSendingUserId != null &&
            friendRequestSendingUserId === rowMenuEntry.userId
          }
          isRemoveMemberSending={removeMemberSending}
          canRemove={creatorId === userId}
          theme={theme}
          onClose={handleCloseMenu}
          onAddFriend={(entry) => onAddFriend?.(entry)}
          onRemoveMember={(entry) => onRemoveMember?.(entry)}
        />
      )}
    </View>
  )
}
